import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Network, Wifi, Tv, Phone, CheckCircle2, ArrowLeft, 
  Clock, Shield, Award, Users, Zap, TrendingUp, 
  Settings, HeadphonesIcon, Star, Globe
} from 'lucide-react';

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();

  const servicesData = {
    'internet-leased-line': {
      id: 'internet-leased-line',
      icon: <Network className="h-16 w-16" />,
      title: 'Internet Leased Line',
      subtitle: 'Dedicated Symmetric Bandwidth for Your Business',
      description: 'A premium internet connectivity solution designed for businesses that demand guaranteed speeds, reliability, and uninterrupted service. Our Internet Leased Line provides dedicated bandwidth exclusively for your organization.',
      bgGradient: 'from-blue-600 via-cyan-500 to-blue-400',
      cardColor: 'border-blue-500',
      image: 'https://images.unsplash.com/photo-1551703599-2a53f65da7e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxmaWJlciUyMGludGVybmV0fGVufDB8fHx8MTc2MjIwNTA2M3ww&ixlib=rb-4.1.0&q=85',
      bannerImage: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxuZXR3b3JrJTIwc2VydmVyfGVufDB8fHx8MTc2MjIwNTA3OHww&ixlib=rb-4.1.0&q=85',
      features: [
        {
          icon: <Shield className="h-8 w-8" />,
          title: 'Dedicated Bandwidth',
          description: 'Exclusive bandwidth that isn\'t shared with others, ensuring consistent performance'
        },
        {
          icon: <Zap className="h-8 w-8" />,
          title: 'Symmetric Speeds',
          description: 'Equal upload and download speeds for seamless two-way communication'
        },
        {
          icon: <Award className="h-8 w-8" />,
          title: 'SLA Guarantee',
          description: 'Service Level Agreement with 99.9% uptime commitment'
        },
        {
          icon: <Clock className="h-8 w-8" />,
          title: '24/7 Support',
          description: 'Round-the-clock technical support and network monitoring'
        }
      ],
      benefits: [
        'Scalable bandwidth options from 10 Mbps to 10 Gbps',
        'Static IP addresses included',
        'Priority support with dedicated account manager',
        'Suitable for VPN, video conferencing, cloud applications',
        'Low latency for real-time applications',
        'Ideal for businesses with heavy internet usage'
      ],
      plans: [
        { speed: '5 Mbps', price: 'Contact Us', features: ['1:1 Ratio', 'Static IP', '99.9% Uptime'] },
        { speed: '10 Mbps', price: 'Contact Us', features: ['1:1 Ratio', 'Static IP', '99.9% Uptime'] },
        { speed: '50 Mbps', price: 'Contact Us', features: ['1:1 Ratio', 'Static IP', '99.9% Uptime'] },
        { speed: '100 Mbps', price: 'Contact Us', features: ['1:1 Ratio', 'Static IP', 'Priority Support'] }
      ]
    },
    'broadband-services': {
      id: 'broadband-services',
      icon: <Wifi className="h-16 w-16" />,
      title: 'Broadband Services',
      subtitle: 'High-Speed Internet for Home & Office',
      description: 'Experience lightning-fast internet connectivity with our fiber optic broadband services. Perfect for homes, small businesses, and remote workers who need reliable, high-speed internet without data caps.',
      bgGradient: 'from-red-600 via-orange-500 to-yellow-400',
      cardColor: 'border-red-500',
      image: 'https://images.unsplash.com/photo-1604869515882-4d10fa4b0492?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxmaWJlciUyMGludGVybmV0fGVufDB8fHx8MTc2MjIwNTA2M3ww&ixlib=rb-4.1.0&q=85',
      bannerImage: 'https://images.unsplash.com/photo-1597733336794-12d05021d510?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxmaWJlciUyMGludGVybmV0fGVufDB8fHx8MTc2MjIwNTA2M3ww&ixlib=rb-4.1.0&q=85',
      features: [
        {
          icon: <Zap className="h-8 w-8" />,
          title: 'Up to 1 Gbps Speed',
          description: 'Blazing-fast download and upload speeds for all your needs'
        },
        {
          icon: <Globe className="h-8 w-8" />,
          title: 'Unlimited Data',
          description: 'No data caps or FUP limits - use as much as you want'
        },
        {
          icon: <Settings className="h-8 w-8" />,
          title: 'Free Installation',
          description: 'Professional installation at no extra cost'
        },
        {
          icon: <HeadphonesIcon className="h-8 w-8" />,
          title: 'Customer Support',
          description: '24/7 support for all your connectivity needs'
        }
      ],
      benefits: [
        'Fiber optic technology for superior reliability',
        'Multiple device connectivity support',
        'Perfect for streaming, gaming, and work from home',
        'Quick installation within 48 hours',
        'No hidden charges or contracts',
        'Ideal for families and small offices'
      ],
      plans: [
        { speed: '5 Mbps', price: '₹299/month', features: ['Unlimited Data', 'Free Installation', 'Basic Support'] },
        { speed: '100 Mbps', price: '₹599/month', features: ['Unlimited Data', 'Free Router', 'Free Installation'] },
        { speed: '200 Mbps', price: '₹799/month', features: ['Unlimited Data', 'Free Router', 'Priority Support'] },
        { speed: '500 Mbps', price: '₹1,299/month', features: ['Unlimited Data', 'Premium Router', 'Dedicated Support'] }
      ]
    },
    'ott-services': {
      id: 'ott-services',
      icon: <Tv className="h-16 w-16" />,
      title: 'OTT Services',
      subtitle: 'Seamless Streaming Experience',
      description: 'Optimized connectivity for all major OTT platforms including Netflix, Amazon Prime, Disney+, and more. Our specialized OTT service ensures buffer-free streaming in 4K quality with minimal latency.',
      bgGradient: 'from-purple-600 via-pink-500 to-rose-400',
      cardColor: 'border-purple-500',
      image: 'https://images.pexels.com/photos/1148820/pexels-photo-1148820.jpeg',
      bannerImage: 'https://images.unsplash.com/photo-1522869635100-9f4c5e86aa37?w=1200&h=400&fit=crop',
      features: [
        {
          icon: <Star className="h-8 w-8" />,
          title: '4K Streaming',
          description: 'Ultra-HD streaming with crystal-clear picture quality'
        },
        {
          icon: <Zap className="h-8 w-8" />,
          title: 'Zero Buffering',
          description: 'Optimized routing for popular streaming platforms'
        },
        {
          icon: <Users className="h-8 w-8" />,
          title: 'Multi-Device',
          description: 'Stream on multiple devices simultaneously'
        },
        {
          icon: <Clock className="h-8 w-8" />,
          title: 'Low Latency',
          description: 'Minimal delay for live sports and events'
        }
      ],
      benefits: [
        'Optimized for Netflix, Prime Video, Disney+, and more',
        'Support for multiple streaming profiles',
        'Compatible with Smart TVs, streaming boxes, and mobile devices',
        'Automatic quality adjustment based on bandwidth',
        'Parental control features available',
        'Access to international content with fast speeds'
      ],
      plans: [
        { speed: '5 Mbps', price: '₹249/month', features: ['SD Streaming', '1 Device', 'Basic Support'] },
        { speed: '50 Mbps', price: '₹499/month', features: ['HD Streaming', '2 Devices', 'Basic Support'] },
        { speed: '100 Mbps', price: '₹699/month', features: ['4K Streaming', '4 Devices', 'Priority Support'] },
        { speed: '200 Mbps', price: '₹999/month', features: ['4K Streaming', 'Unlimited Devices', '24/7 Support'] }
      ]
    },
    'voip-services': {
      id: 'voip-services',
      icon: <Phone className="h-16 w-16" />,
      title: 'VoIP Services',
      subtitle: 'Enterprise Voice Communication',
      description: 'Transform your business communications with our advanced Voice over IP solutions. Get HD voice quality, call recording, virtual numbers, and significant cost savings compared to traditional phone systems.',
      bgGradient: 'from-green-600 via-emerald-500 to-teal-400',
      cardColor: 'border-green-500',
      image: 'https://images.pexels.com/photos/1181316/pexels-photo-1181316.jpeg',
      bannerImage: 'https://images.unsplash.com/photo-1423666639041-f56000c27a9a?w=1200&h=400&fit=crop',
      features: [
        {
          icon: <Phone className="h-8 w-8" />,
          title: 'HD Voice Quality',
          description: 'Crystal-clear audio for professional communication'
        },
        {
          icon: <Settings className="h-8 w-8" />,
          title: 'Call Recording',
          description: 'Automatic call recording for compliance and training'
        },
        {
          icon: <Globe className="h-8 w-8" />,
          title: 'Virtual Numbers',
          description: 'Local and toll-free numbers in multiple cities'
        },
        {
          icon: <TrendingUp className="h-8 w-8" />,
          title: 'Cost Effective',
          description: 'Save up to 60% on communication costs'
        }
      ],
      benefits: [
        'Scalable system that grows with your business',
        'Advanced call management features',
        'Integration with CRM and business tools',
        'Mobile app for calls on the go',
        'Conference calling with up to 100 participants',
        'Auto-attendant and IVR capabilities'
      ],
      plans: [
        { speed: 'Basic', price: '₹299/user/month', features: ['1 Number', 'Basic Features', 'Email Support'] },
        { speed: 'Business', price: '₹599/user/month', features: ['Multiple Numbers', 'Call Recording', 'Priority Support'] },
        { speed: 'Enterprise', price: 'Contact Us', features: ['Unlimited Numbers', 'Advanced Features', 'Dedicated Manager'] }
      ]
    }
  };

  const service = servicesData[serviceId];

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Service Not Found</h1>
          <Button onClick={() => navigate('/')}>Go Back Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section with Gradient Background */}
      <div className={`bg-gradient-to-br ${service.bgGradient} text-white py-20 relative overflow-hidden`}>
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="mb-8 bg-white/20 hover:bg-white/30 text-white border-white/40 backdrop-blur-sm"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="bg-white/20 backdrop-blur-md p-4 rounded-full w-24 h-24 flex items-center justify-center mb-6 border-2 border-white/40">
                {service.icon}
              </div>
              <h1 className="text-5xl font-bold mb-4 drop-shadow-lg">{service.title}</h1>
              <p className="text-2xl mb-6 text-white/90">{service.subtitle}</p>
              <p className="text-lg leading-relaxed text-white/80 mb-8">{service.description}</p>
              <Button
                onClick={() => navigate('/signup')}
                size="lg"
                className="bg-white text-gray-900 hover:bg-gray-100 font-bold text-lg px-8 py-6 shadow-2xl"
              >
                Get Started Now
              </Button>
            </div>
            <div>
              <img
                src={service.image}
                alt={service.title}
                className="rounded-2xl shadow-2xl border-4 border-white/40 w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Key Features Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">Key Features</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {service.features.map((feature, index) => (
              <Card key={index} className={`border-4 ${service.cardColor} hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2`}>
                <CardContent className="p-6 text-center">
                  <div className={`w-16 h-16 mx-auto rounded-full bg-gradient-to-br ${service.bgGradient} flex items-center justify-center mb-4 text-white shadow-lg`}>
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-gray-900">{feature.title}</h3>
                  <p className="text-gray-700">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Banner Image Section */}
      <section className="py-0">
        <div className="relative h-[400px] overflow-hidden">
          <img
            src={service.bannerImage}
            alt="Service Banner"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30 flex items-center justify-center">
            <div className="text-center text-white px-6">
              <h2 className="text-5xl font-bold mb-4 drop-shadow-2xl">Transform Your Connectivity</h2>
              <p className="text-2xl drop-shadow-lg">Experience the difference with {service.title}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className={`py-16 bg-gradient-to-br ${service.bgGradient}`}>
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-12 text-white drop-shadow-lg">Why Choose This Service?</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {service.benefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-4 bg-white/20 backdrop-blur-md rounded-xl p-6 border-2 border-white/40 hover:bg-white/30 transition-all">
                <CheckCircle2 className="h-8 w-8 text-white flex-shrink-0 mt-1" />
                <p className="text-lg text-white font-medium">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Plans Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-4 text-gray-900">Our Plans</h2>
          <p className="text-center text-gray-600 mb-12 text-lg">Choose the perfect plan for your needs</p>
          <div className="grid md:grid-cols-3 gap-8">
            {service.plans.map((plan, index) => (
              <Card key={index} className={`border-4 ${service.cardColor} hover:shadow-2xl transition-all duration-300 transform hover:scale-105 ${index === 1 ? 'md:-translate-y-4' : ''}`}>
                <CardContent className="p-8">
                  <div className={`text-center mb-6 ${index === 1 ? 'bg-gradient-to-r ' + service.bgGradient + ' text-white -mx-8 -mt-8 py-3 mb-8' : ''}`}>
                    <h3 className="text-2xl font-bold mb-2">{plan.speed}</h3>
                    <p className="text-3xl font-bold">{plan.price}</p>
                  </div>
                  <div className="space-y-4 mb-8">
                    {plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <Button
                    onClick={() => navigate('/signup')}
                    className={`w-full font-bold text-lg ${index === 1 ? 'bg-gradient-to-r ' + service.bgGradient + ' text-white' : ''}`}
                    variant={index === 1 ? 'default' : 'outline'}
                  >
                    Choose Plan
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-16 bg-gradient-to-r ${service.bgGradient} text-white`}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl mb-8 text-white/90">
            Join thousands of satisfied customers who trust us for their connectivity needs
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/signup')}
              size="lg"
              className="bg-white text-gray-900 hover:bg-gray-100 font-bold text-lg px-8 py-6"
            >
              Sign Up Now
            </Button>
            <Button
              onClick={() => navigate('/contact')}
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/20 font-bold text-lg px-8 py-6"
            >
              Contact Sales
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
